import type { Modifier } from './stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Les formes que prennent les améliorations.

   Comme pour les ennemis, les TYPES vivent dans la simulation et les DONNÉES
   dans le contenu. Ajouter un mod est un fichier ; ajouter une nouvelle
   MÉCANIQUE de mod est une modification de moteur — et c'est la frontière
   qu'on veut voir.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Les six tags de synergie (PLAN.md §6.7).
 *
 *  Leur rôle n'est pas décoratif : ils sont ce qui transforme une liste de
 *  bonus en BUILD. Sans tags, chaque carte est un choix isolé et le joueur
 *  prend simplement la plus forte ; avec eux, il commence à construire vers
 *  quelque chose, et un choix médiocre peut devenir excellent trois cartes
 *  plus tard. */
export const Tag = {
  Cinetique: 'CINETIQUE',
  Plasma: 'PLASMA',
  Thermique: 'THERMIQUE',
  Drone: 'DRONE',
  Orbital: 'ORBITAL',
  Surcharge: 'SURCHARGE',
} as const;

export type TagId = (typeof Tag)[keyof typeof Tag];

export const Rarity = {
  Common: 0,
  Rare: 1,
  Epic: 2,
  /** Débloquée par les tags, jamais tirée au hasard. */
  Fusion: 3,
} as const;

/* Poids calés sur le banc d'équilibrage, pas devinés. Avec Épique à 16, ces
   mods n'apparaissaient pas dans 15 % des builds : ils occupaient une place
   dans le pool sans jamais être vus, ce qui est du contenu mort. */
export const RARITY_WEIGHT: Readonly<Record<number, number>> = {
  [Rarity.Common]: 100,
  [Rarity.Rare]: 62,
  [Rarity.Epic]: 34,
  // Poids de base d'une Fusion : quasi nul tant que ses conditions ne sont pas
  // remplies. Il bondit à 35 dès qu'elles le sont — c'est le moment « le jeu
  // récompense mon build », et il doit être fréquent et évident.
  [Rarity.Fusion]: 2,
};

export const FUSION_UNLOCKED_WEIGHT = 35;

export interface ModDef {
  readonly id: string;
  readonly name: string;
  readonly rarity: number;
  readonly tags: readonly TagId[];
  readonly modifiers: readonly Modifier[];
  /** Texte affiché sur la carte. ⚠️ Il DOIT être chiffré — « +18 % de cadence »,
   *  jamais « tir plus rapide ». Un joueur qui ne peut pas comparer deux cartes
   *  ne fait pas un choix, il devine. */
  readonly text: string;
  /** Nombre maximal d'exemplaires. `Infinity` par défaut. */
  readonly maxStacks?: number;
  /** Conditions de Fusion : chaque tag doit être présent au moins N fois.
   *
   *  ⚠️ Une Fusion doit CHANGER LA FAÇON DE JOUER, jamais seulement les
   *  chiffres. Si elle se résume à « +80 % de dégâts », c'est un Épique mal
   *  étiqueté — et le joueur qui l'a construite se sent floué. */
  readonly requires?: ReadonlyArray<readonly [TagId, number]>;
}

/** Un mod possédé, avec son nombre d'exemplaires. */
export interface OwnedMod {
  readonly def: ModDef;
  count: number;
}

export function countTag(owned: readonly OwnedMod[], tag: TagId): number {
  let n = 0;
  for (const o of owned) {
    if (o.def.tags.includes(tag)) n += o.count;
  }
  return n;
}

export function fusionUnlocked(def: ModDef, owned: readonly OwnedMod[]): boolean {
  if (!def.requires || def.requires.length === 0) return false;
  return def.requires.every(([tag, n]) => countTag(owned, tag) >= n);
}

export function stacksOf(owned: readonly OwnedMod[], id: string): number {
  return owned.find((o) => o.def.id === id)?.count ?? 0;
}

/** Tous les modificateurs actifs, exemplaires compris. */
export function activeModifiers(owned: readonly OwnedMod[]): Modifier[] {
  const out: Modifier[] = [];
  for (const o of owned) {
    for (let i = 0; i < o.count; i++) out.push(...o.def.modifiers);
  }
  return out;
}

/**
 * Poids de tirage d'un mod dans l'offre courante.
 *
 * Zéro = exclu. C'est ce qui gère les exemplaires maximum et les Fusions
 * verrouillées, sans cas particulier ailleurs.
 */
export function weightOf(def: ModDef, owned: readonly OwnedMod[]): number {
  const max = def.maxStacks ?? Infinity;
  if (stacksOf(owned, def.id) >= max) return 0;

  if (def.requires && def.requires.length > 0) {
    return fusionUnlocked(def, owned) ? FUSION_UNLOCKED_WEIGHT : 0;
  }
  return RARITY_WEIGHT[def.rarity] ?? 10;
}
